
import { useEffect, useMemo } from 'react';
import { DeviceMapProps, MapLocation } from '@/types/location';
import { locations as originalLocations } from '@/data/locations';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { useMediaOwnerCheck } from '@/hooks/useMediaOwnerCheck';
import { useLocationOverrides } from '@/hooks/useLocationOverrides';
import { RegionSelector } from '@/components/map/RegionSelector';
import { SelectedLocationsList } from '@/components/map/SelectedLocationsList';
import { LocationSelectionManager } from '@/components/map/LocationSelectionManager';
import { DeviceMapFilters } from './DeviceMapFilters';
import { DeviceMapContainer } from './DeviceMapContainer';
import { useDeviceMapState } from './useDeviceMapState';
import { Skeleton } from '@/components/ui/skeleton';

interface ExtendedDeviceMapProps extends DeviceMapProps {
  importedLocations?: Partial<import('@/types/location').MapLocation>[];
}

const DeviceMap = ({ onDeviceSelect, onLocationsChange, importedLocations = [] }: ExtendedDeviceMapProps) => {
  const { isAdmin } = useAdminCheck();
  const { isMediaOwner, mediaOwnerName } = useMediaOwnerCheck();
  const { overrides, loading: overridesLoading } = useLocationOverrides();

  // Make sure originalLocations exists and has items
  const availableLocations = useMemo(() =>
    Array.isArray(originalLocations) && originalLocations.length > 0
      ? originalLocations
      : [],
  [originalLocations]);

  // Stable reference — only recomputes when availableLocations changes
  const uniqueLocations = useMemo(() =>
    availableLocations.filter((location, index, self) =>
      index === self.findIndex(l => l.id === location.id)
    ),
  [availableLocations]);

  // Merge DB overrides into hardcoded location data
  const mergedLocations = useMemo<MapLocation[]>(() => {
    return uniqueLocations.map(loc => {
      const override = overrides.get(loc.id);
      if (!override) return loc;

      const extra = override.extra_details ?? {};
      return {
        ...loc,
        // Core field overrides
        ...(override.location_name ? { name: override.location_name } : {}),
        ...(override.screen_count_override !== null && override.screen_count_override !== undefined
          ? { devices: override.screen_count_override }
          : {}),
        ...(override.region ? { region: override.region as MapLocation['region'] } : {}),
        ...(override.screen_type ? { screenType: override.screen_type } : {}),
        ...(override.media_kind ? { mediaKind: override.media_kind as MapLocation['mediaKind'] } : {}),
        // Coordinate override — validate Saudi Arabia bounds (lat: 15–35°N, lng: 34–57°E)
        // to guard against swapped lat/lng values saved in extra_details
        ...(extra.lat !== undefined && extra.lng !== undefined &&
            extra.lat >= 15 && extra.lat <= 35 && extra.lng >= 34 && extra.lng <= 57
          ? { coordinates: [extra.lat, extra.lng] as [number, number] }
          : {}),
        // extra_details fields merged into .details
        details: {
          ...(loc.details ?? {}),
          // Use 'key in extra' instead of !== undefined so empty strings are also accepted
          ...('description' in extra ? { description: extra.description } : {}),
          ...('screenDimensions' in extra ? { screenDimensions: extra.screenDimensions } : {}),
          ...('resolution' in extra ? { resolution: extra.resolution } : {}),
        },
        // Audience overrides
        ...(extra.gender ? { gender: extra.gender } : {}),
        ...(extra.ageRange ? { ageRange: extra.ageRange } : {}),
        ...(extra.segment ? { segment: extra.segment } : {}),
        ...(extra.orientation ? { orientation: extra.orientation as MapLocation['orientation'] } : {}),
        // Bug 6 fix: propagate is_available so the map can filter out unavailable locations
        isAvailable: override.is_available,
        // Propagate maintenance status so the popup can show a badge
        maintenanceStatus: override.maintenance_status ?? undefined,
        maintenanceNotes: override.maintenance_notes ?? undefined,
      } as MapLocation;
    });
  }, [uniqueLocations, overrides]);

  // Filter out adminOnly locations for non-privileged users, and is_available=false from DB overrides
  const visibleLocations = useMemo(() => {
    const base = mergedLocations.filter(loc => {
      if (!loc.adminOnly) return true;
      if (isAdmin) return true;
      if (isMediaOwner && mediaOwnerName && loc.mediaOwner === mediaOwnerName) return true;
      return false;
    });
    return base.filter(loc => loc.isAvailable !== false);
  }, [mergedLocations, isAdmin, isMediaOwner, mediaOwnerName]);

  const [state, actions] = useDeviceMapState({
    onDeviceSelect,
    onLocationsChange,
    importedLocations,
    originalLocations: visibleLocations
  });

  const {
    selectedLocations,
    activeRegion,
    locations,
    activeMediaKind,
    activeScreenType,
    activeMediaOwner,
    filteredLocations,
    attentionScreenCounts,
    isRemoving
  } = state;

  const {
    setActiveRegion,
    setActiveMediaKind,
    setActiveScreenType,
    setActiveMediaOwner,
    handleToggleAllInRegion,
    handleToggleTypeInRegion,
    handleDeselectLocation,
    handleAttentionScreenCountChange
  } = actions;

  // Force map rerender on mount
  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, []);

  // Show a skeleton while DB overrides are loading to prevent content jumping
  if (overridesLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full rounded-xl" />
        <Skeleton className="h-10 w-full rounded-xl" />
        <Skeleton className="h-[400px] w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4">
        <DeviceMapFilters
          activeMediaKind={activeMediaKind}
          setActiveMediaKind={setActiveMediaKind}
          activeScreenType={activeScreenType}
          setActiveScreenType={setActiveScreenType}
          activeMediaOwner={activeMediaOwner}
          setActiveMediaOwner={setActiveMediaOwner}
        />
        
        <RegionSelector
          activeRegion={activeRegion}
          activeMediaOwner={activeMediaOwner}
          activeMediaKind={activeMediaKind}
          onRegionChange={setActiveRegion}
          onToggleAll={handleToggleAllInRegion}
          onToggleType={handleToggleTypeInRegion}
          getLocationTypes={() => LocationSelectionManager.getLocationTypes(activeRegion, locations)}
          selectedLocations={selectedLocations}
          locations={locations}
        />
      </div>

      <DeviceMapContainer 
        state={state} 
        actions={actions}
        attentionScreenCounts={attentionScreenCounts}
        onAttentionScreenCountChange={handleAttentionScreenCountChange}
        overrides={overrides}
      />

      <SelectedLocationsList
        selectedLocations={selectedLocations}
        locations={locations}
        onDeselect={handleDeselectLocation}
        attentionScreenCounts={attentionScreenCounts}
        isRemoving={isRemoving}
      />
    </div>
  );
};

export default DeviceMap;
